import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toggleCategory, deleteCategory, setRoundsDaysBeforeDate } from '../actions';
import { Button } from 'react-bootstrap';
import AddMenuItemModal from './modals/add_menu_item_modal';
import AddSubcategoryModal from './modals/add_subcategory_modal';
import CategoryTable from './categories/category_table';
import MobilePreview from './mobile_preview';
import { useCategoryHandlers } from '../hooks/useCategoryHandlers';
import DirectoriesTable from './directories_table';

const AppDashboardLayout = () => {
  const buttons = useSelector(state => state.buttons);
  const directories = useSelector(state => state.directories);
  const clubLogo = useSelector(state => state.club_logo);
  const canDashboardItems = useSelector(state => state.dashboardItems);
  const clubName = useSelector(state => state.club_name);
  const roundsDaysBeforeDate = useSelector(state => state.rounds_days_before_date);
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState({});
  const categories = buttons.filter(btn => btn.parent_id === null);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);

  useEffect(() => {
    window.glg.app_state = {
      buttons,
      directories,
      rounds_days_before_date: roundsDaysBeforeDate,
    };
  }, [buttons, directories, roundsDaysBeforeDate]);

  useEffect(() => {
    window.glg.is_dirty = false;

    const handleBeforeUnload = (e) => {
      if (window.glg.is_dirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);


  const {
    handleSaveNewCategory,
    handleSaveNewSubcategory,
    handleUpdateCategory,
    handleUpdateSubcategory,
  } = useCategoryHandlers({ setShowMenuModal, setShowSubModal, setEditMode, setItemToEdit });

  const handleEditClick = (item) => {
    setItemToEdit(item);
    setEditMode(true);
    item.parent_id === null ? setShowMenuModal(true) : setShowSubModal(true);
  };

  const handleDelete = (id) => {
    dispatch(deleteCategory(id));
  };

  const grouped = categories
    .sort((a, b) => (a.order_number ?? 0) - (b.order_number ?? 0))
    .map(category => ({
      ...category,
      subcategories: buttons
        .filter(btn => btn.parent_id === category.id)
        .sort((a, b) => (a.order_number ?? 0) - (b.order_number ?? 0))
    }));

  const toggleExpanded = id => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <React.Fragment>
      <AddMenuItemModal
        show={showMenuModal}
        onHide={() => {
          setShowMenuModal(false);
          setEditMode(false);
          setItemToEdit(null);
        }}
        onSave={editMode ? handleUpdateCategory : handleSaveNewCategory}
        initialData={itemToEdit}
        editMode={editMode}
      />

      <AddSubcategoryModal
        show={showSubModal}
        onHide={() => {
          setShowSubModal(false);
          setEditMode(false);
          setItemToEdit(null);
        }}
        onSave={editMode ? handleUpdateSubcategory : handleSaveNewSubcategory}
        parentCategories={categories}
        initialData={itemToEdit}
        editMode={editMode}
      />

      <div className="col-md-7 app_dashboard">

        <div className='page_title'>
          <div className="text after_text_1">{window.I18n.t('mobile_app_dashboard.home.menu_items')}</div>
        </div>

        {canDashboardItems && (
          <div>
            <div className='btsp_bullet_button pointer btsp_text_15' onClick={() => setShowMenuModal(true)}>
              <div className='icon'><i className='gga-circle-plus'></i></div>
              <div className='btsp_align-middle'>{window.I18n.t('mobile_app_dashboard.home.new_menu_item')}</div>
            </div>

            <div className='btsp_bullet_button pointer btsp_text_15' onClick={() => setShowSubModal(true)}>
              <div className='icon'><i className='gga-circle-plus'></i></div>
              <div className='btsp_align-middle'>{window.I18n.t('mobile_app_dashboard.home.new_submenu_item')}</div>
            </div>
          </div>
        )}

        <p className="text-muted">
          {window.I18n.t('mobile_app_dashboard.home.select_menu_and_submenu')}
        </p>

        <CategoryTable
          grouped={grouped}
          expanded={expanded}
          toggleExpanded={toggleExpanded}
          handleEditClick={handleEditClick}
          handleDelete={handleDelete}
          toggleCategory={toggleCategory}
        />

        <div className='page_title directories-title'>
          <div className="text after_text_1">{window.I18n.t('mobile_app_dashboard.home.directories')}</div>
        </div>

        <DirectoriesTable />

        <div className="page_title home-settings-title">
          <div className="text after_text_1">{!window.I18n ? '' : window.I18n.t('mobile_app_dashboard.home.home_settings')}</div>
        </div>

        <p>
          {!window.I18n ? '' : window.I18n.t('mobile_app_dashboard.home.rounds_before_date_prefix')}
          {' '}
          <input
            type="number"
            min="0"
            max="90"
            step={1}
            value={roundsDaysBeforeDate}
            onChange={(e) => {
              const val = Math.min(90, Math.max(0, parseInt(e.target.value, 10) || 0));
              dispatch(setRoundsDaysBeforeDate(val));
            }}
            style={{ width: '60px', display: 'inline-block' }}
          />
          {' '}
          {!window.I18n ? '' : window.I18n.t('mobile_app_dashboard.home.rounds_before_date_suffix')}
        </p>
      </div>

      <MobilePreview grouped={grouped} clubLogo={clubLogo} clubName={clubName} />
    </React.Fragment>
  );
};

export default AppDashboardLayout;
